import {
  calculateArrGapResult,
  levelProfileFor,
  type ArrGapAnswers,
  type ArrGapContext,
  type ArrGapRisk,
  type ArrGapSuggestedLevel,
} from "./arrGapCheck";

export type ArrGapReportInput = {
  sessionId: string;
  name: string;
  email?: string;
  unsubscribeUrl?: string;
  resubscribeUrl?: string;
  readiness?: number;
  suggestedLevel?: string;
  context: Record<string, string>;
  answers: Record<string, number>;
  firstFix?: string;
  secondRisk?: string;
  watchItem?: string;
  aiNotLever?: boolean;
};

export type ArrGapReportBlock = {
  heading?: string;
  lines: string[];
  button?: { label: string; href: string };
  secondary?: { label: string; href?: string };
};

export type ArrGapReportEmail = {
  subject: string;
  preheader: string;
  text: string;
  html: string;
  headers: Record<string, string>;
};

const BOOKING_ORIGIN = "https://www.aldersearch.com";
const REPLY_ADDRESS = "ryan@aldersearch.com";
const PREHEADER = "Your score, your level, what to budget and the first thing to fix.";
const FORMULA = "Each answer scores 1 to 4. Your score is your total, rescaled so 6 is 0 and 24 is 100.";
const UNSUBSCRIBE_ORIGIN = "https://aldersearch.com";

const questionOrder = [
  ["decision-rights", "Decision rights"],
  ["mandate", "Mandate"],
  ["commercial-ownership", "Commercial ownership"],
  ["customer-evidence", "Customer evidence"],
  ["ai", "AI"],
  ["hiring-process", "Hiring process"],
] as const;

const LEADER_DIMENSIONS = "problem fit, commercial ownership, technical fluency, customer depth, AI credibility, leadership fit, operating fit, mandate fit";
const PM_DIMENSIONS = "outcome ownership, discovery, cross-functional collaboration, prioritisation, technical fluency, AI delivery (weight only if the role needs it), level fit";

const OPENING_BY_SITUATION: Record<string, string> = {
  "live-role-now": "You said you have a live role. Here is what to settle before the next round of interviews.",
  "hiring-6-to-18-months": "You said you are hiring in the next 6 to 18 months. That is the right time to settle these.",
  "not-sure-what-we-need": "You said you are not sure what you need yet. This is what your answers say.",
};

const BUDGET_STATUS: Record<string, string> = {
  fixed: "Your budget is fixed. If the best candidate needs 10 to 15 percent above band, decide now who can approve it within 48 hours.",
  "stretch-15-percent": "You can stretch about 15 percent for the right person. Agree who approves it, so an offer can go out within 48 hours.",
  "not-agreed-with-board": "Your budget is not agreed with the board. Settle it before the first interview, not at offer. Pay expectations should be agreed at the first screen.",
};

type LeadershipBudgetCell = { range?: string; note?: string; text?: string; absent?: boolean };

const LEADERSHIP_BUDGETS: Record<Exclude<ArrGapSuggestedLevel, "Senior PM" | "Lead PM" | "Principal PM">, Record<"Seed" | "Series A/B" | "Growth/PE", LeadershipBudgetCell>> = {
  "Head of Product": {
    Seed: { range: "£90k to £130k", note: "Equity does real work, reports to the founder." },
    "Series A/B": { range: "£115k to £145k", note: "Equity meaningful but diluting." },
    "Growth/PE": { range: "£115k to £145k", note: "Usually beneath a Director, VP or CPTO rather than the CEO." },
  },
  "Director of Product": {
    Seed: { absent: true },
    "Series A/B": { range: "£140k to £180k", note: "Appears in larger Series B businesses, often owning AI, data or a second product line, bonus and equity on top." },
    "Growth/PE": { range: "£140k to £180k", note: "Common in buy-and-builds and carve-outs." },
  },
  "VP Product": {
    Seed: { absent: true },
    "Series A/B": { range: "£150k to £180k", note: "Appears near the top of the stage around £15m+ ARR, up to £200k in regulated or enterprise contexts." },
    "Growth/PE": { range: "£150k to £180k", note: "Up to £220k for senior technical VPs. Sweet equity typically vests over a 3 to 5 year hold. Bonus inconsistent outside fintech." },
  },
  "CPO / CPTO": {
    Seed: { absent: true },
    "Series A/B": { text: "CPO / CPTO at Series A/B: rare, base moves toward the Director ceiling plus meaningful equity." },
    "Growth/PE": { range: "£200k to £250k", note: "Plus bonus and sweet equity. Frequently CPTO rather than standalone CPO." },
  },
};

function escapeHtml(value: string) {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#39;");
}

function firstName(name: string) {
  const withoutBrackets = name.replace(/\([^)]*\)|\[[^\]]*\]/g, " ").trim();
  return withoutBrackets.split(/\s+/).filter(Boolean)[0] ?? "";
}

function isTestLead(name: string, email?: string) {
  return /test|audit/i.test(name) || (email?.trim().toLowerCase().split("@")[1] === "aldersearch.com");
}

function scoreLabel(score: number) {
  return ({ 1: "Missing", 2: "Gap", 3: "Solid", 4: "Strong" } as Record<number, string>)[score] ?? "";
}

function reportTrack(context: Record<string, string>) {
  return context.questionTrack === "Senior PM" ? "Senior PM" : "Leader";
}

function hasLeadershipLevel(level: ArrGapSuggestedLevel): level is Exclude<ArrGapSuggestedLevel, "Senior PM" | "Lead PM" | "Principal PM"> {
  return level === "Head of Product" || level === "Director of Product" || level === "VP Product" || level === "CPO / CPTO";
}

function leadershipStage(stage?: string): "Seed" | "Series A/B" | "Growth/PE" {
  if (stage === "seed") return "Seed";
  if (stage === "series-a" || stage === "series-b") return "Series A/B";
  return "Growth/PE";
}

function pmStage(stage?: string): "Seed" | "Series A/B" | "Series C+" {
  if (stage === "seed") return "Seed";
  if (stage === "series-a" || stage === "series-b") return "Series A/B";
  return "Series C+";
}

function bookingUrl(sessionId: string) {
  return `${BOOKING_ORIGIN}/book?sid=${encodeURIComponent(sessionId)}`;
}

function comparisonUrl(input: ArrGapReportInput) {
  return `${BOOKING_ORIGIN}/arr-hiring-gap-check?compare=${encodeURIComponent(input.sessionId)}&hirer_role=${encodeURIComponent(input.context.hirerRole ?? "")}&hiring_for=${encodeURIComponent(input.context.hiringFor ?? "")}&track=${encodeURIComponent(input.context.questionTrack ?? "Leader")}`;
}

function investorWording(context: Record<string, string>) {
  return context.hirerRole === "investor-talent-leader";
}

function investorVoice(value: string) {
  return value
    .replaceAll("You're", "The company is")
    .replaceAll("you're", "the company is")
    .replaceAll("You've", "The company has")
    .replaceAll("you've", "the company has")
    .replaceAll("You have", "The company has")
    .replaceAll("you have", "the company has")
    .replaceAll("You are", "The company is")
    .replaceAll("you are", "the company is")
    .replaceAll("Your", "The company's")
    .replaceAll("your", "the company's")
    .replaceAll("you", "the company");
}

function riskCopy(risk: ArrGapRisk, context: Record<string, string>, aiNotLever: boolean) {
  const pm = reportTrack(context) === "Senior PM";
  const investor = investorWording(context);
  const settleHeading = context.hiringSituation === "live-role-now" ? "Settle before the next round" : "Settle before the search";
  const score = risk.score;
  let means = "";
  let costs = "";
  let settle = "";
  let probe: string | null = null;
  let watch = "";

  if (risk.title === "Decision rights") {
    means = pm
      ? "The decisions this person owns are not yet clear to them or to the team."
      : investor
        ? "The CEO is hiring someone to own decisions the CEO has not yet let go of."
        : "You are hiring someone to own decisions you have not yet let go of.";
    costs = pm
      ? "A PM who cannot decide becomes a ticket-taker. The team goes round them, and good candidates spot it and leave."
      : "A new leader who cannot decide becomes a well-paid observer. The team learns to go round them. Senior candidates spot this quickly and drop out.";
    settle = pm
      ? "Write down the product area and the decisions this person owns. Tell the team before the offer goes out."
      : investor
        ? "Ask the CEO to name three decisions the CEO will stop making on day one. Write them down. Tell the team and the board."
        : "Name three decisions you will stop making on day one. Write them down. Tell the team and the board.";
    probe = pm
      ? "Tell me about a call you made that engineering or design disagreed with. What happened?"
      : "Tell me about a decision you made that the CEO disagreed with. What happened?";
    watch = "You have handed over some decisions. Make the boundary explicit so the team stops guessing.";
  }

  if (risk.title === "Mandate") {
    means = score === 1
      ? investor
        ? "The CEO has not asked the people who matter what this hire must change, so the business is assuming they agree."
        : "You have not asked the people who matter what this hire must change, so you are assuming they agree."
      : "The people who matter use the same words to mean different things.";
    costs = "The spec turns into a wish list, the panel debates the candidate instead of the role, and good candidates sense the confusion.";
    settle = pm
      ? investor
        ? "Ask the CEO, the engineering lead and the design lead separately what this hire must change in six months. Write one sentence with a measure and get all three to sign it."
        : "Ask the hiring manager, the engineering lead and the design lead separately what this hire must change in six months. Write one sentence with a measure and get all three to sign it."
      : investor
        ? "Ask the CEO, CTO and lead investor separately what this hire must change in 12 months. Write one sentence with a measure and get all three to sign it."
        : "Ask your CEO, CTO and lead investor separately what this hire must change in 12 months. Write one sentence with a measure and get all three to sign it.";
    probe = "What was the metric when you started, what was it when you left, and what did you personally change?";
    watch = "You agree on the problem. Write it down with a measure so it survives the first disagreement.";
  }

  if (risk.title === "Commercial ownership") {
    means = score === 1 ? "Product is judged on what ships, not on a number." : "The number that matters belongs to sales or customer success, not to product.";
    costs = investor
      ? "The business ends up hiring a delivery manager and wondering why nothing moves. Candidates spot an execution-only job dressed up as a product role."
      : "You end up hiring a delivery manager and wondering why nothing moves. Candidates spot an execution-only job dressed up as a product role.";
    settle = investor
      ? "Ask the CEO to pick one number (activation, NRR, win rate or churn), agree today's baseline and put it in this person's goals before the offer."
      : "Pick one number (activation, NRR, win rate or churn), agree today's baseline and put it in this person's goals before the offer.";
    if (!pm) settle += " For mid-market B2B SaaS, good looks like NRR above 110 percent, CAC payback under 12 months and LTV to CAC above 3 to 1.";
    probe = "What was the number when you started, what did you change, and what is it now?";
    watch = "Product tracks the number but it is not in anyone's goals. Put it in the hire's goals and review it monthly.";
  }

  if (risk.title === "Customer evidence") {
    means = score === 1 ? "Roadmap changes are driven by the loudest executive." : "Roadmap changes are driven by the latest big deal.";
    costs = "The new hire inherits a roadmap they cannot defend and spends the first quarter arguing about it. A hire who runs weekly discovery will clash with a team that does not.";
    settle = investor
      ? "Ask the CEO to write down the last three roadmap changes and what triggered each. If the trail is thin, agree what evidence a change needs before this person starts."
      : "Write down the last three roadmap changes and what triggered each. If the trail is thin, agree what evidence a change needs before this person starts.";
    probe = "Walk me through the last time customer evidence changed your mind about what to build.";
    watch = "Customer feedback and opinion are mixed. Agree what evidence a change needs.";
  }

  if (risk.title === "AI") {
    means = score === 1
      ? investor ? "Nobody owns the question of what AI is for in the business." : "Nobody owns the question of what AI is for in your business."
      : investor ? "The company has shipped AI but has not checked whether it earns back its cost." : "You have shipped AI but have not checked whether it earns back its cost.";
    costs = score === 1
      ? investor ? "The company cannot say what it is hiring someone to do about it, so it ends up screening for buzzwords." : "You cannot say what you are hiring someone to do about it, so you end up screening for buzzwords."
      : "Without cost and quality tracked, the hire inherits a margin question they cannot answer.";
    settle = pm
      ? "Write down what you expect from a PM in an AI-assisted team, including evals, quality bars and cost. Weight AI delivery in the scorecard only if the role needs it."
      : "Decide whether AI is a feature, a margin lever or the product over the next 18 months. The answer changes who you hire.";
    if (investor) settle = pm
      ? "Ask the CEO to write down what the business expects from a PM in an AI-assisted team, including evals, quality bars and cost. Weight AI delivery in the scorecard only if the role needs it."
      : "Ask the CEO to decide whether AI is a feature, a margin lever or the product over the next 18 months. The answer changes who you hire.";
    if (aiNotLever) settle = `AI is not your main lever, but your board will ask. ${settle}`;
    probe = "What did your evals catch that users would otherwise have seen? What did it cost per user, and what did that do to margin?";
    watch = "You have agreed where AI can help, but how you judge quality and cost is still a guess. Write down the quality bar and the cost ceiling this hire will be held to.";
  }

  if (risk.title === "Hiring process") {
    means = score === 1
      ? investor ? "The company's last senior hire was decided on gut feel after a good conversation." : "Your last senior hire was decided on gut feel after a good conversation."
      : investor ? "Each interviewer asked their own questions, then the company talked it over." : "Each interviewer asked their own questions, then you talked it over.";
    costs = "Interviewers disagree and nobody can say why. Interviewer pairs often differ by a point or more, and a typical hire takes around 35 to 45 days and 15 to 20 interviews. Basis: Alder Search placement data and live process experience, not a published benchmark.";
    settle = investor
      ? "Ask the CEO to agree a scorecard before the first interview. Score independently, then compare. Aim for an offer within 21 days of the first interview."
      : "Agree a scorecard before the first interview. Score independently, then compare. Aim for an offer within 21 days of the first interview.";
    watch = "You had criteria but they shifted. Agree the scorecard before the first interview and hold it.";
  }

  if (investor) {
    means = investorVoice(means);
    costs = investorVoice(costs);
    settle = investorVoice(settle);
    probe = probe ? investorVoice(probe) : probe;
    watch = investorVoice(watch);
  }
  if (risk.score === 3) return { heading: `Worth watching: ${risk.title}`, lines: [`What this means: ${watch}`] };
  return {
    heading: risk.title,
    lines: [
      `What this means: ${means}`,
      `What it costs: ${costs}`,
      `${settleHeading}: ${settle}`,
      ...(probe ? [`Interview probe: "${probe}"`] : []),
    ],
  };
}

function budgetLines(level: ArrGapSuggestedLevel, context: Record<string, string>) {
  const lines: string[] = [];
  if (hasLeadershipLevel(level)) {
    const stage = leadershipStage(context.stage);
    const cell = LEADERSHIP_BUDGETS[level][stage];
    if (cell.absent) {
      lines.push(`A ${level} is rare at ${stage}. Check whether your problem needs a lower level.`);
    } else {
      lines.push(cell.text ?? `${level} at ${stage}: ${cell.range} base. ${cell.note ?? ""}`);
      lines.push("Base salary in GBP, UK market. Netherlands, Belgium and Germany differ; ask for a local view.");
      lines.push("Basis: top-quartile London, from live conversations and placements at Alder Search. Regional UK roles typically run 15 to 20 percent lower on base.");
      if (context.stage === "seed" || context.stage === "series-a" || level === "CPO / CPTO") lines.push("This figure is informed judgement, not placement data.");
      if ((level === "CPO / CPTO" || level === "VP Product") && stage === "Growth/PE") lines.push("Management sweet equity sits behind the debt and the fund's loan notes, so ask for the waterfall, not the headline.");
    }
  } else {
    const stage = pmStage(context.stage);
    if (level === "Senior PM") lines.push(`UK average base for a Senior PM at ${stage}: ${({ Seed: "£87,600", "Series A/B": "£103,900", "Series C+": "£117,200" } as Record<string, string>)[stage]}. Basis: UK tech companies, 2025.`);
    if (level === "Senior PM") {
      const adverts = context.stage === "pe-backed"
        ? "Live London adverts for a Senior PM, all employers: £80k to £105k, range £50k to £150k. Basis: live London adverts, Sep 2026, small samples."
        : "Live London adverts for a Senior PM, VC-backed: £90k to £108.5k, range £70k to £150k, top end is AI and late-stage fintech. Basis: live London adverts, Sep 2026, small samples.";
      lines.push(adverts);
    }
    if (level === "Lead PM") lines.push("Live London adverts for a Lead PM: £92.5k to £115k, up to £125k to £145k at a late-stage fintech. Basis: live London adverts, Sep 2026, small samples.");
    if (level === "Principal PM") lines.push("Principal / Staff PM: £110k to £130k. Basis: UK candidate expectations, May to Sep 2026, because no bands are advertised.");
    lines.push("Senior ICs at Series A typically get options worth 20 to 50 percent of base. Basis: leading VC guidance.");
    lines.push("EMI now covers companies up to 500 staff for options granted from 6 April 2026. Basis: UK EMI rules.");
    lines.push("Bonus is usually not the lever. If you hire from a corporate, expect to buy out a 10 to 15 percent bonus in base or options. Basis: live London adverts, Sep 2026, small samples.");
    if (context.mainLever === "new-product-ai") lines.push("For a PM who has shipped AI to production, expect to pay about 10 to 15 percent above band, with more of it in options. Basis: European tech, IC level.");
  }
  const budgetStatus = BUDGET_STATUS[context.budgetFlexibility ?? ""] ?? "";
  lines.push(investorWording(context) ? budgetStatus.replace(/^Your /, "The company's ").replace(/^You can /, "The company can ") : budgetStatus);
  return lines.filter(Boolean);
}

function processLines(level: ArrGapSuggestedLevel, context: Record<string, string>) {
  const pm = reportTrack(context) === "Senior PM";
  const first = context.hardestPart === "knowing-who-is-good"
    ? `Score every candidate against one agreed scorecard, independently, then compare. Interviewer pairs often differ by a point or more, and the debate is where the signal is. ${pm ? `PM scorecard dimensions: ${PM_DIMENSIONS}.` : `Leader scorecard dimensions: ${LEADER_DIMENSIONS}.`}`
    : context.hardestPart === "attracting-risk-company"
      ? "Speed and clarity win. Offer acceptance is typically 80 to 85 percent across the market, and the median from first to last interview is typically 14 to 18 days. Aim for an offer within 21 days of the first interview."
      : "Pay for the job, not the location: set one band per location. At Series A, equity does more of the work than cash.";
  const shared = "Across the market, a typical hire takes around 35 to 45 days and 15 to 20 interviews. Basis: Alder Search placement data and live process experience, not a published benchmark.";
  const process = pm
    ? [
        "Screen, 30 minutes: level fit and stage fit, agree pay expectations now.",
        "Hiring manager deep dive, 60 minutes: one product they owned, the metric when they started, when they left, what they personally changed.",
        "Working session, 60 to 90 minutes: a real trade-off from your product shared in advance, no generic take-home, cap preparation at 2 hours.",
        "Team panel, 60 minutes: an engineering lead and a designer, each scoring independently before anyone discusses the candidate.",
      ]
    : ["Agree the scorecard before the first interview and score independently."];
  return [first, shared, ...process];
}

export function workshopRouteForSituation(situation?: string, lowestArea = "the area that scored lowest") {
  if (situation === "live-role-now") return {
    description: `Your lowest area is ${lowestArea}. The workshop starts there. Product Hiring Strategy Workshop. 30 minutes, free, one to one. For a live role it covers the level, the mandate, the decisions to hand over, the success measure and the budget.`,
    bookingDescription: "We settle the level, the mandate, the decisions you will hand over, the success measure and the budget.",
    secondary: "Already hiring? Reply with the role and I will tell you what I would settle first.",
  };
  if (situation === "hiring-6-to-18-months") return {
    description: `Your lowest area is ${lowestArea}. The workshop starts there. Product Hiring Strategy Workshop. 30 minutes, free, one to one. For a future hire it covers what the business needs the hire to change and how to prepare before opening a search.`,
    bookingDescription: "We work out what the business needs this hire to change, whether the level is right, and how to prepare before you open a search.",
    secondary: "Reply and I will send details.",
  };
  return {
    description: `Your lowest area is ${lowestArea}. The workshop starts there. Reply and I will send the next date.`,
    bookingDescription: "We work out what the business needs this hire to change, whether the level is right, and how to prepare before you open a search.",
    secondary: undefined,
  };
}

function routeBlock(input: ArrGapReportInput, lowestArea?: string) {
  const route = workshopRouteForSituation(input.context.hiringSituation, lowestArea);
  if (investorWording(input.context)) route.description = route.description.replace("Your lowest area is", "The company's lowest area is");
  const book = { label: "Book a Product Hiring Strategy Workshop", href: bookingUrl(input.sessionId) };
  if (input.context.hiringSituation !== "not-sure-what-we-need") return {
    heading: "YOUR NEXT STEP",
    lines: [route.description],
    button: book,
    secondary: route.secondary ? { label: route.secondary } : undefined,
  };
  return {
    heading: "YOUR NEXT STEP",
    lines: [route.description],
    secondary: book,
  };
}

function reportData(input: ArrGapReportInput) {
  const track = reportTrack(input.context);
  const result = calculateArrGapResult(input.answers as ArrGapAnswers, input.context as ArrGapContext, track);
  if (!result) throw new Error("A complete ARR report requires all six behavioural answers.");
  return { result, profile: levelProfileFor(result.suggestedLevel) };
}

function plainText(greeting: string, opening: string, blocks: ArrGapReportBlock[], unsubscribeUrl: string, resubscribeUrl: string) {
  const sections = [greeting, opening];
  for (const block of blocks) {
    const entries = [block.heading ?? "", ...block.lines];
    if (block.button) entries.push(`${block.button.label}: ${block.button.href}`);
    if (block.secondary) entries.push(block.secondary.href ? `${block.secondary.label}: ${block.secondary.href}` : block.secondary.label);
    sections.push(entries.filter(Boolean).join("\n"));
  }
  sections.push("Send this to your co-founder or a board member. If your answers don't match, you're not yet hiring for the same job.");
  sections.push("Questions first? Just reply to this email.\n\nRyan Ollerenshaw\nAlder Search\naldersearch.com");
  sections.push(`You received this because you asked for your report at aldersearch.com. Unsubscribe: ${unsubscribeUrl}\nUnsubscribed by mistake? Resubscribe: ${resubscribeUrl}\nAlder Search Ltd, company number 17132288`);
  return sections.filter(Boolean).join("\n\n");
}

function reportHtml(greeting: string, opening: string, blocks: ArrGapReportBlock[], unsubscribeUrl: string, resubscribeUrl: string) {
  const blockHtml = blocks.map((block) => {
    const heading = block.heading ? `<h2 style="font-size:16px;letter-spacing:0.08em;margin:30px 0 12px;color:#173d2d">${escapeHtml(block.heading)}</h2>` : "";
    const lines = block.lines.map((line) => `<p style="margin:0 0 12px">${escapeHtml(line)}</p>`).join("");
    const button = block.button ? `<p style="margin:20px 0"><a href="${escapeHtml(block.button.href)}" style="display:inline-block;background:#173d2d;color:#ffffff;padding:14px 20px;text-decoration:none;font-weight:700">${escapeHtml(block.button.label)}</a></p>` : "";
    const secondary = block.secondary ? `<p style="margin:12px 0"><a href="${escapeHtml(block.secondary.href ?? `mailto:${REPLY_ADDRESS}`)}" style="color:#173d2d;text-decoration:underline">${escapeHtml(block.secondary.label)}</a></p>` : "";
    return `${heading}${lines}${button}${secondary}`;
  }).join("");
  return `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent">${escapeHtml(PREHEADER)}</div><div style="max-width:600px;margin:0 auto;padding:24px;font-family:Arial,sans-serif;font-size:16px;line-height:1.6;color:#173d2d"><p style="margin:0 0 18px">${escapeHtml(greeting)}</p><p style="margin:0 0 18px">${escapeHtml(opening)}</p>${blockHtml}<p style="margin:30px 0 12px">${escapeHtml("Send this to your co-founder or a board member. If your answers don't match, you're not yet hiring for the same job.")}</p><p style="margin:0 0 24px">Questions first? Just reply to this email.<br><br>Ryan Ollerenshaw<br>Alder Search<br>aldersearch.com</p><p style="border-top:1px solid #dfe6df;padding-top:18px;margin:0;font-size:12px;color:#587063">You received this because you asked for your report at aldersearch.com. <a href="${escapeHtml(unsubscribeUrl)}" style="color:#587063">Unsubscribe</a><br>Unsubscribed by mistake? <a href="${escapeHtml(resubscribeUrl)}" style="color:#587063">Resubscribe</a><br>Alder Search Ltd, company number 17132288</p></div>`;
}

export function buildArrGapReport(input: ArrGapReportInput) {
  return buildArrGapEmail(input).text;
}

export function getArrGapReportView(input: ArrGapReportInput) {
  const { result, profile } = reportData(input);
  const first = firstName(input.name);
  const greeting = first ? `Hi ${first},` : "Hi,";
  const opening = OPENING_BY_SITUATION[input.context.hiringSituation ?? ""] ?? OPENING_BY_SITUATION["not-sure-what-we-need"];
  const firstFix = result.firstFix;
  const reportRisks = result.risks;
  const profileLines = [
    `Hire this level when: ${profile.hireThisWhen}`,
    `What they own: ${profile.whatTheyOwn}`,
    `Measured on: ${profile.measuredOn}`,
    `Watch for: ${profile.redFlag}`,
  ];
  if (profileLines.some((line) => /\d/.test(line))) profileLines.push("Basis: Alder practitioner view, not a public standard.");
  const blocks: ArrGapReportBlock[] = [
    {
      heading: "YOUR RESULT",
      lines: [
        `Readiness: ${result.readiness}/100. ${result.readinessBand}.`,
        FORMULA,
        ...questionOrder.map(([id, label]) => `${label}: ${scoreLabel(input.answers[id] ?? 0)}`),
      ],
    },
    {
      heading: "YOUR LEVEL",
      lines: [
        `Recommended level: ${result.suggestedLevel}.`,
        ...profileLines,
        ...(result.levelRecommendation.note ? [result.levelRecommendation.note] : []),
      ],
    },
    { heading: "WHAT TO BUDGET", lines: budgetLines(result.suggestedLevel, input.context) },
    {
      heading: "THE TWO THINGS MOST LIKELY TO STALL THIS HIRE",
      lines: reportRisks.length ? [] : ["No major risk found. Keep scoring candidates against one agreed scorecard."],
    },
  ];

  if (result.levelRecommendation.whyNot) {
    const lean = input.context.lean === "cpo-or-cpto" ? "CPO or CPTO" : input.context.lean === "vp" ? "VP" : input.context.lean === "director" ? "Director" : input.context.lean === "head-of-product" ? "Head of Product" : input.context.lean === "senior-pm" ? "Senior PM" : input.context.lean === "lead-pm" ? "Lead PM" : input.context.lean === "principal-pm" ? "Principal PM" : "level";
    blocks.splice(2, 0, { heading: `Why not a ${lean}`, lines: [result.levelRecommendation.whyNot] });
  }

  for (const risk of reportRisks) blocks.push(riskCopy(risk, input.context, result.aiNotLever));
  if (reportRisks.length < 2 && result.watchItem) blocks.push(riskCopy(result.watchItem, input.context, result.aiNotLever));

  blocks.push(
    { heading: "HOW TO RUN THE PROCESS", lines: processLines(result.suggestedLevel, input.context) },
    routeBlock(input, result.firstFix?.title ?? result.watchItem?.title),
    { heading: "COMPARE YOUR ANSWERS", lines: [], button: { label: "Send them the quiz.", href: comparisonUrl(input) } },
  );

  return { greeting, opening, blocks, result, profile };
}

export function buildArrGapEmail(input: ArrGapReportInput): ArrGapReportEmail {
  const { greeting, opening, blocks, result } = getArrGapReportView(input);
  const unsubscribeUrl = input.unsubscribeUrl ?? `${UNSUBSCRIBE_ORIGIN}/unsubscribe?token=preview`;
  const resubscribeUrl = input.resubscribeUrl ?? `${UNSUBSCRIBE_ORIGIN}/resubscribe?token=preview`;
  const firstFix = result.firstFix;
  const subjectCore = firstFix
    ? `Your ARR Hiring Gap Check: ${result.readiness}/100. First thing to fix: ${firstFix.title}`
    : `Your ARR Hiring Gap Check: ${result.readiness}/100. No major risk found`;
  const test = isTestLead(input.name, input.email);
  return {
    subject: test ? `[TEST] ${subjectCore}` : subjectCore,
    preheader: PREHEADER,
    text: plainText(greeting, opening, blocks, unsubscribeUrl, resubscribeUrl),
    html: reportHtml(greeting, opening, blocks, unsubscribeUrl, resubscribeUrl),
    headers: { ...(test ? { "X-Alder-Test": "true" } : {}) },
  };
}
